import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Loader2, ExternalLink } from 'lucide-react';
import axios from 'axios';

export const Login = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    otp: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success('Login successful!');
        navigate('/portal');
      } else {
        if (showOtpInput) {
          // Verify OTP
          const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
          const response = await axios.post(`${BACKEND_URL}/api/auth/verify-otp`, {
            email: registeredEmail,
            otp: formData.otp
          });
          
          toast.success('Email verified! You can now login.');
          setShowOtpInput(false);
          setIsLogin(true);
          setFormData({ name: '', email: '', password: '', phone: '', otp: '' });
        } else {
          // Register and send OTP
          const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
          const response = await axios.post(`${BACKEND_URL}/api/auth/register`, {
            name: formData.name,
            email: formData.email,
            password: formData.password,
            phone: formData.phone
          });
          
          setRegisteredEmail(formData.email);
          setShowOtpInput(true);
          toast.success('OTP sent to your email! Please verify to complete registration.');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setLoading(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      await axios.post(`${BACKEND_URL}/api/auth/resend-otp`, { email: registeredEmail });
      toast.success('OTP resent successfully!');
    } catch (error) {
      toast.error('Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background py-24" data-testid="login-page">
      <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8">
        {/* NJ Portal Links */}
        <div className="mb-6 space-y-3" data-testid="nj-portal-links">
          <a
            href="https://www.njindiaonline.in/cdesk/login.fin"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between w-full p-4 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity"
            data-testid="nj-client-portal-link"
          >
            <div className="flex items-center gap-3">
              <ExternalLink className="h-5 w-5" />
              <div>
                <p className="font-semibold text-sm">NJ Client Portal</p>
                <p className="text-xs opacity-80">Access your NJ India investment account</p>
              </div>
            </div>
            <span className="text-xs opacity-70">njindiaonline.in</span>
          </a>
          <a
            href="https://ewa.njindiaonline.com/ewa/login"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between w-full p-4 bg-secondary text-secondary-foreground rounded-lg hover:opacity-90 transition-opacity"
            data-testid="nj-ewealth-portal-link"
          >
            <div className="flex items-center gap-3">
              <ExternalLink className="h-5 w-5" />
              <div>
                <p className="font-semibold text-sm">NJ E-Wealth Portal</p>
                <p className="text-xs opacity-80">Manage your E-Wealth account</p>
              </div>
            </div>
            <span className="text-xs opacity-70">ewa.njindiaonline.com</span>
          </a>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl font-playfair text-center" data-testid="auth-title">
              {showOtpInput ? 'Verify Email OTP' : (isLogin ? 'Login to Client Portal' : 'Create Account')}
            </CardTitle>
            {showOtpInput && (
              <p className="text-sm text-center text-muted-foreground mt-2">
                We've sent a 6-digit OTP to {registeredEmail}
              </p>
            )}
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {showOtpInput ? (
                // OTP Verification Form
                <>
                  <div>
                    <Label htmlFor="otp">Enter OTP</Label>
                    <Input
                      id="otp"
                      value={formData.otp}
                      onChange={(e) => setFormData({ ...formData, otp: e.target.value })}
                      required
                      maxLength={6}
                      placeholder="Enter 6-digit OTP"
                      data-testid="otp-input"
                      className="text-center text-2xl tracking-widest"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={loading || formData.otp.length !== 6}
                    data-testid="verify-otp-button"
                  >
                    {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Verify OTP
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={handleResendOtp}
                    disabled={loading}
                    data-testid="resend-otp-button"
                  >
                    Resend OTP
                  </Button>
                </>
              ) : (
                // Login/Register Form
                <>
                  {!isLogin && (
                    <div>
                      <Label htmlFor="name">Full Name</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        data-testid="register-name-input"
                      />
                    </div>
                  )}
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      data-testid="auth-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      data-testid="auth-password-input"
                    />
                  </div>
                  {!isLogin && (
                    <div>
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        required
                        data-testid="register-phone-input"
                      />
                    </div>
                  )}
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={loading}
                    data-testid="auth-submit-button"
                  >
                    {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {isLogin ? 'Login' : 'Register & Send OTP'}
                  </Button>
                </>
              )}
            </form>
            {!showOtpInput && (
              <div className="mt-4 text-center">
                <button
                  onClick={() => {
                    setIsLogin(!isLogin);
                    setFormData({ name: '', email: '', password: '', phone: '', otp: '' });
                  }}
                  className="text-sm text-secondary hover:underline"
                  data-testid="auth-toggle-button"
                >
                  {isLogin ? "Don't have an account? Register" : 'Already have an account? Login'}
                </button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;
